'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEditorSession } from '@/hooks/useEditorSession';
import { decideEntryState } from '@/lib/editor/session';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { EditorShell } from './EditorShell';
import { EmptyState } from './EmptyState';
import { ProjectHub, ProjectSummary } from './ProjectHub';

type View =
  | { screen: 'loading' }
  | { screen: 'empty' }
  | { screen: 'hub' }
  | { screen: 'editor'; projectId: string };

export function EditorEntry() {
  const { authedFetch } = useAuthToken();
  const { getSession, touch } = useEditorSession();
  const [view, setView] = useState<View>({ screen: 'loading' });
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [busy, setBusy] = useState(false);

  const loadProjects = useCallback(async (): Promise<ProjectSummary[]> => {
    const res = await authedFetch('/api/editor-projects?limit=24');
    const json = await res.json();
    const list: ProjectSummary[] =
      json?.success && Array.isArray(json.projects)
        ? json.projects.map((p: any) => ({
            id: p.id,
            title: p.title,
            status: p.status,
            aspectRatio: p.aspectRatio,
            updatedAt: p.updatedAt,
            thumbnailUrl: p.clips?.[0]?.src,
          }))
        : [];
    setProjects(list);
    return list;
  }, [authedFetch]);

  // Первичное решение экрана входа
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadProjects();
      if (cancelled) return;
      const decision = decideEntryState({
        projectIds: list.map((p) => p.id),
        session: getSession(),
        now: Date.now(),
      });
      if (decision.screen === 'resume') {
        setView({ screen: 'editor', projectId: decision.projectId });
      } else {
        setView({ screen: decision.screen });
      }
    })().catch(() => setView({ screen: 'hub' }));
    return () => {
      cancelled = true;
    };
  }, [getSession, loadProjects]);

  const openProject = useCallback(
    (id: string) => {
      touch(id);
      setView({ screen: 'editor', projectId: id });
    },
    [touch]
  );

  const createProject = useCallback(async () => {
    setBusy(true);
    try {
      const res = await authedFetch('/api/editor-projects', {
        method: 'POST',
        body: JSON.stringify({
          title: 'Без названия',
          aspectRatio: '9:16',
          duration: 0,
          clips: [],
          layers: [],
          assets: [],
        }),
      });
      const json = await res.json();
      if (json?.success && json.project?.id) {
        openProject(json.project.id);
      }
    } catch (e) {
      console.error('createProject failed', e);
    } finally {
      setBusy(false);
    }
  }, [authedFetch, openProject]);

  const deleteProject = useCallback(
    async (id: string) => {
      try {
        const res = await authedFetch(`/api/editor-projects/${id}`, { method: 'DELETE' });
        const json = await res.json();
        if (!res.ok || !json?.success) throw new Error(json?.error || 'delete failed');
        const list = await loadProjects();
        setView({ screen: list.length === 0 ? 'empty' : 'hub' });
      } catch (e) {
        console.error('deleteProject failed', e);
      }
    },
    [authedFetch, loadProjects]
  );

  const exitToHub = useCallback(async () => {
    setView({ screen: 'loading' });
    try {
      const list = await loadProjects();
      setView({ screen: list.length === 0 ? 'empty' : 'hub' });
    } catch {
      setView({ screen: projects.length === 0 ? 'empty' : 'hub' });
    }
  }, [loadProjects, projects.length]);

  if (view.screen === 'loading') {
    // Full-screen overlay — covers the studio sidebar/header so the workspace
    // doesn't "flash" the layout before the editor is ready.
    return (
      <div className="fixed inset-0 z-[60] bg-[#0e0a16]">
        <LoadingScreen label="Загружаем рабочее пространство…" fill />
      </div>
    );
  }
  if (view.screen === 'empty') {
    return <EmptyState onCreate={createProject} busy={busy} />;
  }
  if (view.screen === 'hub') {
    return (
      <ProjectHub
        projects={projects}
        onOpen={openProject}
        onCreate={createProject}
        onDelete={deleteProject}
        busy={busy}
      />
    );
  }
  // Полноэкранный редактор поверх студийного layout (сайдбар/шапка не мешают).
  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-[#0e0a16]">
      <EditorShell projectId={view.projectId} onExitToHub={exitToHub} />
    </div>
  );
}
